import * as React from 'react'
import { PropsWithChildren, ReactElement, useEffect } from 'react'
import { CreateDto } from '../../../config/filePartyConfig'
import { FormikConfig } from 'formik/dist/types'
import { AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import { Formik, useFormikContext } from 'formik'
import { PartyAction } from '../../../config/partyConfig'
import Form from '../Form'
import { AccountingId } from '../../../config/finance/invoiceConfig'
import FormikField from '../../Formik/FormikField'
import EnumCombobox, { EnumComboboxProps } from '../../Combobox/EnumCombobox'
import { AttributeEntityDto } from '../../../hooks/useAttributes'

export type BasePartyDto = AttributeEntityDto & {
    id?: string
    addressStreet: string
    addressNumber: string
    addressPostalCode: string
    addressLocality: string
    addressCountry: string
    email: string
    phoneLandline: string
    status: PartyStatus
    type: PartyType
    pivot?: CreateDto
    externals: { [unitKey: string]: AccountingId }
}

export type AddPartyProps = Pick<CreateDto, 'role' | 'parentId'>

export enum PartyStatus {
    Active = 'ACTIVE',
    Inactive = 'INACTIVE',
}

export enum PartyType {
    Legal = 'LEGAL',
    Natural = 'NATURAL',
}

export function PartyStatusDropdown(): ReactElement {
    const context = useFormikContext<BasePartyDto>()

    return (
        <FormikField<EnumComboboxProps<PartyStatus>>
            labelKey={'party.status.label'}
            context={context}
            fieldName={'status'}
            component={EnumCombobox}
            componentProps={{
                entities: [PartyStatus.Active, PartyStatus.Inactive],
                labelKeyPrefix: 'party.status',
                selectedOptions: [context.values.status],
                onOptionSelect: (_, data) => context.setFieldValue('status', data.selectedOptions?.[0]),
                clearable: false,
            }}
        />
    )
}

export type PartyProps<D extends BasePartyDto> = {
    formikProps: Omit<FormikConfig<D>, 'onSubmit'>
    onSubmit: (party: D) => Promise<AxiosResponse<string>>
    goBack: () => void
}

export default function PartyForm<D extends BasePartyDto>(props: PropsWithChildren<PartyProps<D>>): ReactElement {
    const dispatch = useDispatch()

    useEffect(() => {
        return () => {
            dispatch({ type: PartyAction.Reset })
        }
    }, [])

    return (
        <Formik<D>
            {...props.formikProps}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (party: D) => {
                await props.onSubmit({
                    ...party,
                    pivot: party.pivot ?? undefined,
                })
                props.goBack()
            }}
        >
            <Form>{props.children}</Form>
        </Formik>
    )
}
